// src/hooks/useSectionScrollSpy.ts
//
// Which of a set of same-page sections is currently in view.
//
// Written for the patient dashboard (ClickUp 86d41947z), where the sidebar section entries are
// anchors rather than routes, so `NavItem`'s route-matching `isActive` has nothing to match on.
// In the collapsed 64px rail the patient gets by default, the highlight is the ONLY positional
// feedback available — the labels are hidden — so this is what makes the icons navigation
// rather than a row of shortcuts.
//
// ⚠️ **THE ONE EXISTING PRECEDENT WAS CHECKED AND IS NOT REUSABLE.** `GuideArticlePage.tsx`
// renders antd's `<Anchor>`. That is a self-contained widget: it draws its own list of links,
// keeps the active item as private internal state, and writes `location.hash` on click. It
// exposes no "which section is in view" value that a foreign component could render from, so it
// cannot drive `isActive` on the sidebar's custom `NavItem`s — and the hash write is exactly
// what this hook must not do (see below). Nothing was lifted from it.
//
// ⚠️ **IntersectionObserver, never a scroll listener.** A scroll handler on a page this size
// fires hundreds of times a second and would need throttling; the observer is native and
// passive.
//
// ⚠️ **This hook never writes `location.hash`.** Updating the hash on scroll fights
// react-router, adds a history entry per section, and would make the browser back button walk
// back up the page instead of leaving the screen.

import { useEffect, useRef, useState } from "react";

/** Top of the observation band: clear of the 44px fixed top bar plus a little breathing room. */
const DEFAULT_ROOT_MARGIN = "-60px 0px -55% 0px";

/**
 * Returns the DOM id of the section currently in view, or `undefined` before the first
 * observation resolves.
 *
 * `sectionIds` is the ordered list of section wrapper ids — order matters, see the tie-break
 * below. Callers should memoise it, but the hook keys its effect on the joined string rather
 * than array identity so a caller who forgets does not thrash the observer.
 */
export function useSectionScrollSpy(
	sectionIds: string[],
	options?: { rootMargin?: string },
): string | undefined {
	const [activeId, setActiveId] = useState<string | undefined>(undefined);
	const rootMargin = options?.rootMargin ?? DEFAULT_ROOT_MARGIN;

	// Identity-stable key. A caller rendering `["a","b"]` inline would otherwise tear down and
	// rebuild the observer on every parent render — and the patient dashboard has ~20 pieces of
	// state above this.
	const idsKey = sectionIds.join("|");

	// The observer callback only receives the entries that CHANGED, so the full intersecting
	// set has to be carried across invocations.
	const intersectingRef = useRef<Set<string>>(new Set());

	useEffect(() => {
		const ids = idsKey ? idsKey.split("|") : [];
		if (ids.length === 0) return;

		// jsdom does not implement IntersectionObserver, and neither does a server render.
		// Degrade to "no highlight" rather than throwing — the nav still scrolls, it just does
		// not light up.
		if (typeof IntersectionObserver === "undefined") return;

		const intersecting = intersectingRef.current;
		intersecting.clear();

		const observer = new IntersectionObserver(
			(entries) => {
				for (const entry of entries) {
					const id = entry.target.id;
					if (entry.isIntersecting) intersecting.add(id);
					else intersecting.delete(id);
				}

				// ⚠️ **THE TIE-BREAK IS THE POINT OF THIS BLOCK.** The dashboard is a 2-column
				// grid, so two sections sit side by side and intersect at the SAME scroll
				// offset. Picking `entries[0]` or "the largest ratio" makes the highlight
				// flicker between the left and right column. Earliest in `ids` order wins,
				// which — because rail order equals scroll order (decision D5) — is also the
				// one visually highest on the page.
				const next = ids.find((id) => intersecting.has(id));

				// ⚠️ **STICKY when nothing intersects.** Between two sections, or scrolled past
				// the last one on a short viewport, the set goes empty. Clearing the highlight
				// there would blink it off and back on as the patient scrolls, and the ticket
				// asks for deliberate behaviour rather than undefined-flicker. So the last
				// resolved section stays lit until a different one resolves.
				if (next !== undefined) setActiveId(next);
			},
			{ rootMargin, threshold: 0 },
		);

		for (const id of ids) {
			const el = document.getElementById(id);
			// A section can legitimately be absent — a peptide-only patient renders 5 of the 8.
			// The caller passes an already-filtered list, so this is a belt-and-braces guard and
			// deliberately silent: warning here would fire on every such patient.
			if (el) observer.observe(el);
		}

		return () => {
			observer.disconnect();
			intersecting.clear();
		};
	}, [idsKey, rootMargin]);

	// A section that has disappeared (a gate flipped, or the list shrank) must not stay lit.
	return activeId !== undefined && sectionIds.includes(activeId) ? activeId : undefined;
}

export default useSectionScrollSpy;
